import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Database, Search, ChevronRight, FileText, Users, Wallet, Wheat, MapPin, ArrowRight, Download } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const CATEGORY_META = {
  platform: { color: "#00ff41", label: "Platform-Generated", count: 40, icon: Database, desc: "Live data from platform activity" },
  ai: { color: "#0ea5e9", label: "AI Intelligence", count: 7, icon: FileText, desc: "GPT-5.2 generated intelligence" },
  seeded: { color: "#eab308", label: "Seeded Reference", count: 14, icon: MapPin, desc: "Official source reference data" },
  simulated: { color: "#a855f7", label: "Simulated APIs", count: 9, icon: Wallet, desc: "Swap-ready API simulations" },
  external: { color: "#f97316", label: "External APIs", count: 11, icon: ArrowRight, desc: "External data connections" },
  derived: { color: "#06b6d4", label: "Derived/Calculated", count: 11, icon: Wheat, desc: "Computed analytics data" },
};

export default function DataExplorerPage() {
  const [categories, setCategories] = useState([]);
  const [selectedCat, setSelectedCat] = useState("platform");
  const [selectedType, setSelectedType] = useState(null);
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const { data } = await api.get("/categories");
        setCategories(data);
      } catch (err) {
        console.error("Failed to load categories:", err);
      }
    };
    fetchCategories();
  }, []);

  const fetchData = async (category, dataType) => {
    setLoading(true);
    try {
      const { data: res } = await api.get(`/data/${category}/${dataType}`);
      setData(res.data || []);
      setTotal(res.total || 0);
    } catch (err) {
      console.error("Failed to load data:", err);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleTypeSelect = (type) => {
    setSelectedType(type);
    fetchData(selectedCat, type);
  };

  const handleExport = async (dataType) => {
    try {
      const token = localStorage.getItem("auth_token");
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/export/csv/${dataType}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) throw new Error("Export failed");
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${dataType}_export.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success(`Exported ${dataType} to CSV`);
    } catch (err) {
      toast.error("Export failed: " + err.message);
    }
  };

  const currentCategory = categories.find(c => c.id === selectedCat);
  const filteredTypes = (currentCategory?.types || []).filter(t => t.toLowerCase().includes(search.toLowerCase()));

  return (
    <div data-testid="data-explorer-page" className="p-4 md:p-6 space-y-4">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold uppercase tracking-wider text-zinc-100">
          Data Explorer
        </h1>
        <p className="text-xs text-zinc-500 font-mono mt-1">Browse 92 data types across 6 categories</p>
      </div>

      {/* Category Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {Object.entries(CATEGORY_META).map(([key, meta]) => {
          const apiCat = categories.find(c => c.id === key);
          const count = apiCat?.count || meta.count;
          return (
            <Button
              key={key}
              data-testid={`cat-tab-${key}`}
              variant="outline"
              size="sm"
              className={`rounded-none text-[10px] uppercase tracking-wider font-mono whitespace-nowrap shrink-0 ${
                selectedCat === key
                  ? "border-b-2 bg-zinc-900"
                  : "border-zinc-800 text-zinc-500 hover:text-zinc-300"
              }`}
              style={selectedCat === key ? { borderBottomColor: meta.color, color: meta.color } : {}}
              onClick={() => { setSelectedCat(key); setSelectedType(null); setData([]); }}
            >
              {meta.label} ({count})
            </Button>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* Type Browser */}
        <Card className="bg-zinc-950/50 border-zinc-800 rounded-none">
          <CardHeader className="pb-2 px-4 pt-4">
            <CardTitle className="text-[10px] text-zinc-500 uppercase tracking-wider font-mono">
              Data Types ({filteredTypes.length})
            </CardTitle>
            <div className="relative mt-2">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-600" />
              <Input
                data-testid="type-search-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="filter types..."
                className="rounded-none border-zinc-800 bg-black font-mono text-xs pl-8 h-8 focus:ring-1 focus:ring-[#00ff41]"
              />
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <ScrollArea className="h-[500px]">
              <div className="px-2 py-1">
                {filteredTypes.map((type) => (
                  <button
                    key={type}
                    data-testid={`type-item-${type}`}
                    className={`w-full text-left px-3 py-2 text-[11px] font-mono flex items-center justify-between transition-colors duration-100 ${
                      selectedType === type
                        ? "bg-zinc-800 text-zinc-100"
                        : "text-zinc-400 hover:bg-zinc-900 hover:text-zinc-200"
                    }`}
                    onClick={() => handleTypeSelect(type)}
                  >
                    <span className="truncate">{type.replace(/_/g, " ")}</span>
                    <ChevronRight className="w-3 h-3 flex-shrink-0 text-zinc-600" />
                  </button>
                ))}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Data View */}
        <Card className="lg:col-span-3 bg-zinc-950/50 border-zinc-800 rounded-none">
          <CardHeader className="pb-2 px-4 pt-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-xs font-mono text-zinc-300 flex items-center gap-2">
                <Database className="w-3.5 h-3.5" style={{ color: CATEGORY_META[selectedCat]?.color }} />
                {selectedType ? selectedType.replace(/_/g, " ") : "Select a data type"}
                {total > 0 && <Badge variant="outline" className="rounded-none text-[9px] border-zinc-700 font-mono">{total} records</Badge>}
              </CardTitle>
              {data.length > 0 && (
                <Button
                  data-testid="export-csv-btn"
                  variant="outline"
                  size="sm"
                  className="rounded-none border-zinc-700 text-xs font-mono h-7 gap-1"
                  onClick={() => handleExport(selectedType)}
                >
                  <Download className="w-3 h-3" /> Export CSV
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            {loading ? (
              <div className="space-y-2">
                {[...Array(5)].map((_, i) => <div key={i} className="h-8 bg-zinc-900 animate-pulse" />)}
              </div>
            ) : data.length > 0 ? (
              <ScrollArea className="h-[500px]">
                <table className="w-full data-table">
                  <thead>
                    <tr>
                      {Object.keys(data[0]).filter(k => k !== "id" && k !== "_id").slice(0, 8).map((key) => (
                        <th key={key}>{key.replace(/_/g, " ")}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.map((row, i) => (
                      <tr key={i}>
                        {Object.entries(row).filter(([k]) => k !== "id" && k !== "_id").slice(0, 8).map(([key, val], j) => (
                          <td key={j} className={typeof val === "number" ? (val >= 0 ? "text-[#00ff41]" : "text-[#ff3b30]") : ""}>
                            {typeof val === "object" ? JSON.stringify(val).substring(0, 40) : String(val ?? "-").substring(0, 50)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </ScrollArea>
            ) : selectedType ? (
              <div className="text-center py-16 text-zinc-600 font-mono text-sm">
                <Database className="w-8 h-8 mx-auto mb-3 text-zinc-700" />
                <p>No data available for this type</p>
                <p className="text-[10px] mt-1">Run the data seeder or start an agent to populate</p>
              </div>
            ) : (
              <div className="text-center py-16 text-zinc-600 font-mono text-sm">
                <FileText className="w-8 h-8 mx-auto mb-3 text-zinc-700" />
                <p>Select a data type from the left panel</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
